/**
 * routeLegs.ts — one set of numbers for the whole trip.
 *
 * The planner used to show three different answers for the same drive: the
 * header took Google's route summary (727 km · 8h 4m for Melbourne →
 * Adelaide), each leg chip used a straight-line estimate at 70 km/h (654 km ·
 * 9h 20m) and the vehicle-aware GraphHopper route had its own figure again.
 *
 * Now there is ONE source: the vehicle-aware route. Its total distance and
 * time are split across the legs by where each stop sits on the polyline, and
 * rounded so the legs add up to exactly the total the header shows. Only when
 * that route isn't available do we fall back (the Google road legs the map drew, then a
 * flagged estimate).
 */

import type { TripLeg, TripLegSource, TripStop } from '../types/trip';
import { buildTripLegs, haversineDistanceKm } from './tripPlanner';
import { roundDriveMinutes } from './tripMapRoute';

export interface LatLngPoint {
  lat: number;
  lng: number;
}

/** vehicle = GraphHopper route for the rig · google = the Google road route the
 *  map drew · estimate = straight line, flagged "≈". */
export type LegSource = 'vehicle' | 'google' | 'estimate';

export interface SplitRoute {
  legs: { distanceKm: number; driveMinutes: number }[];
  /** Polyline vertex index each stop snapped to (monotonic). */
  vertexIndex: number[];
  /** Cumulative km at each polyline vertex. */
  cumulativeKm: number[];
}

/** Cumulative haversine km along a polyline. */
export function cumulativeKm(polyline: LatLngPoint[]): number[] {
  const out = new Array<number>(polyline.length).fill(0);
  for (let i = 1; i < polyline.length; i += 1) {
    const a = polyline[i - 1];
    const b = polyline[i];
    out[i] = out[i - 1] + haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
  }
  return out;
}

/**
 * Round a list of non-negative numbers to integers whose sum is exactly
 * `Math.round(total)` (largest-remainder method). Keeps "legs add up to the
 * header" true after rounding.
 */
export function roundToTotal(values: number[], total: number): number[] {
  const target = Math.round(total);
  if (!values.length) return [];
  const sum = values.reduce((a, b) => a + b, 0);
  const scaled = sum > 0 ? values.map((v) => (v / sum) * target) : values.map(() => target / values.length);
  const floors = scaled.map((v) => Math.floor(v));
  let remainder = target - floors.reduce((a, b) => a + b, 0);
  const order = scaled
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floors];
  for (let k = 0; k < order.length && remainder > 0; k += 1) {
    out[order[k].i] += 1;
    remainder -= 1;
  }
  return out;
}

/**
 * Polyline vertex each stop sits on: the first and last stops are the ends,
 * each middle stop snaps to the nearest vertex at or after the previous
 * stop's (so a route that doubles back still splits in order).
 */
export function snapStopsToPolyline(polyline: LatLngPoint[], stops: LatLngPoint[]): number[] {
  const vertexIndex: number[] = [0];
  let from = 0;
  for (let s = 1; s < stops.length - 1; s += 1) {
    const p = stops[s];
    // Leave room for the remaining stops so indices stay strictly ordered.
    const maxIdx = Math.max(from, polyline.length - 1 - (stops.length - 1 - s));
    let best = from;
    let bestD = Infinity;
    for (let i = from; i <= maxIdx; i += 1) {
      const q = polyline[i];
      const d = (q.lat - p.lat) ** 2 + ((q.lng - p.lng) * Math.cos((p.lat * Math.PI) / 180)) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    vertexIndex.push(best);
    from = best;
  }
  vertexIndex.push(polyline.length - 1);
  return vertexIndex;
}

/** The routed line cut into one piece per leg (stop i-1 → stop i). */
export function splitPathAtStops(polyline: LatLngPoint[], stops: LatLngPoint[]): LatLngPoint[][] {
  if (polyline.length < 2 || stops.length < 2) return [];
  const idx = snapStopsToPolyline(polyline, stops);
  const out: LatLngPoint[][] = [];
  for (let i = 1; i < idx.length; i += 1) out.push(polyline.slice(idx[i - 1], idx[i] + 1));
  return out;
}

/**
 * Split a routed polyline's totals across the legs between `stops`.
 * Each stop snaps to the nearest polyline vertex at or after the previous
 * stop's vertex (so a route that doubles back still splits in order).
 * Returns null when the inputs can't be split meaningfully.
 */
export function splitRouteIntoLegs(
  polyline: LatLngPoint[],
  stops: LatLngPoint[],
  totalKm: number,
  totalMinutes: number
): SplitRoute | null {
  if (polyline.length < 2 || stops.length < 2) return null;
  if (!(totalKm > 0) || !(totalMinutes > 0)) return null;

  const cum = cumulativeKm(polyline);
  const vertexIndex = snapStopsToPolyline(polyline, stops);

  const rawKm = vertexIndex.slice(1).map((idx, i) => Math.max(0, cum[idx] - cum[vertexIndex[i]]));
  const km = roundToTotal(rawKm, totalKm);
  const minutes = roundToTotal(rawKm, totalMinutes);
  return {
    legs: km.map((d, i) => ({ distanceKm: d, driveMinutes: minutes[i] })),
    vertexIndex,
    cumulativeKm: cum,
  };
}

/** The point `fraction` (0..1, by distance) of the way between two vertices. */
export function pointAlongPolyline(
  polyline: LatLngPoint[],
  cum: number[],
  startIdx: number,
  endIdx: number,
  fraction: number
): LatLngPoint | null {
  if (!polyline.length || startIdx < 0 || endIdx >= polyline.length || endIdx < startIdx) return null;
  const f = Math.max(0, Math.min(1, fraction));
  const target = cum[startIdx] + (cum[endIdx] - cum[startIdx]) * f;
  for (let i = startIdx + 1; i <= endIdx; i += 1) {
    if (cum[i] >= target) {
      const segLen = cum[i] - cum[i - 1];
      const t = segLen > 0 ? (target - cum[i - 1]) / segLen : 0;
      const a = polyline[i - 1];
      const b = polyline[i];
      return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
    }
  }
  return polyline[endIdx];
}

/** Straight-line interpolation fallback when there's no routed polyline. */
export function interpolate(a: LatLngPoint, b: LatLngPoint, fraction: number): LatLngPoint {
  const f = Math.max(0, Math.min(1, fraction));
  return { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f };
}

/** Build TripLeg[] (keyed by stop id) from per-leg figures. Routed figures
 *  are road figures unless the caller says otherwise. */
export function legsForStops(
  stops: TripStop[],
  figures: { distanceKm?: number; driveMinutes?: number; via?: string }[],
  source: TripLegSource = 'road'
): TripLeg[] {
  const out: TripLeg[] = [];
  for (let i = 1; i < stops.length; i += 1) {
    const f = figures[i - 1];
    const leg: TripLeg = {
      fromId: stops[i - 1].id,
      toId: stops[i].id,
      distanceKm: f?.distanceKm,
      driveMinutes: f?.driveMinutes,
      source,
    };
    if (f?.via) leg.via = f.via;
    out.push(leg);
  }
  return out;
}

export interface FigureSet {
  /** Stop-id key the figures were computed for (stale figures are ignored). */
  key: string;
  legs: { distanceKm: number; driveMinutes: number; via?: string }[];
}

/**
 * Which leg figures the planner shows — one distance truth.
 *
 * The vehicle-aware route (GraphHopper) is signed-in only: it costs per
 * request, so a guest never triggers it. Google's per-leg figures come from
 * the road line the map has ALREADY drawn, so showing them costs nothing —
 * a guest sees the same road figures as the line on the map instead of a
 * straight-line guess. Order: vehicle route (signed in), then Google, then
 * the estimate, which the UI labels "approx.".
 */
export function pickTripLegs(
  stops: TripStop[],
  opts: {
    signedIn: boolean;
    stopIdsKey: string;
    vehicle?: FigureSet | null;
    google?: FigureSet | null;
    /** Road figures remembered per stop pair (see rememberRoadLegs). */
    cache?: RoadLegCache | null;
    /**
     * One set of figures for everyone: the road route (Google) at the rig's
     * pace comes first, signed in or not, so the same trip never reads
     * 13h 7m signed in and 13h 17m signed out. The vehicle route still
     * draws the line; its figures are the fallback when there is no road
     * route yet.
     */
    preferRoad?: boolean;
  }
): { legs: TripLeg[]; source: LegSource } {
  const { signedIn, stopIdsKey, vehicle, google, cache, preferRoad = false } = opts;
  if (stops.length >= 2) {
    // Drive times to the nearest 5 min, rounded once here so every surface
    // (list, map labels, phone sheet, print) shows the same figure.
    const rounded = (figs: FigureSet['legs']) =>
      figs.map((f) => ({ ...f, driveMinutes: roundDriveMinutes(f.driveMinutes) }));
    const vehicleOk = signedIn && !!vehicle && vehicle.key === stopIdsKey && vehicle.legs.length === stops.length - 1;
    const googleOk = !!google && google.key === stopIdsKey && google.legs.length === stops.length - 1;
    if (preferRoad && googleOk) return { legs: legsForStops(stops, rounded(google!.legs)), source: 'google' };
    if (vehicleOk) return { legs: legsForStops(stops, rounded(vehicle!.legs)), source: 'vehicle' };
    if (googleOk) return { legs: legsForStops(stops, rounded(google!.legs)), source: 'google' };
  }
  const estimate = buildTripLegs(stops);
  if (!cache || !Object.keys(cache).length) return { legs: estimate, source: 'estimate' };
  // While a new order is being routed, every pair the road route has already
  // measured keeps its road figures, and the rest are estimated from how much
  // longer than a straight line this trip's roads have turned out to be, so a
  // reorder doesn't swing the totals by 100 km for a second.
  const calib = calibration(cache);
  let anyEstimate = false;
  const legs = estimate.map((leg, i) => {
    // The same road back is (near enough) the same drive.
    const hit = cache[pairKey(stops[i], stops[i + 1])] ?? cache[pairKey(stops[i + 1], stops[i])];
    if (hit) return { ...leg, distanceKm: hit.distanceKm, driveMinutes: roundDriveMinutes(hit.driveMinutes), source: 'road' as const };
    anyEstimate = true;
    const straight = straightKm(stops[i], stops[i + 1]);
    if (calib && typeof straight === 'number') {
      const km = Math.round(straight * calib.kmFactor);
      return { ...leg, distanceKm: km, driveMinutes: Math.round(km * calib.minPerKm) };
    }
    return leg;
  });
  return { legs, source: anyEstimate ? 'estimate' : google ? 'google' : 'vehicle' };
}

/* ── Road figures remembered per pair of stops ──────────────────────────── */

export interface RoadLegFigure {
  distanceKm: number;
  driveMinutes: number;
  /** Straight-line km between the two stops (for calibrating estimates). */
  straightKm: number;
}
export type RoadLegCache = Record<string, RoadLegFigure>;

/** A leg's identity: both stop ids and where they are (~1 km). Moving a stop
 *  makes it a new leg. */
export function pairKey(a: TripStop, b: TripStop): string {
  const at = (s: TripStop) => (typeof s.lat === 'number' && typeof s.lng === 'number' ? `${s.lat.toFixed(2)},${s.lng.toFixed(2)}` : '-');
  return `${a.id}@${at(a)}>${b.id}@${at(b)}`;
}

function straightKm(a: TripStop, b: TripStop): number | undefined {
  if (typeof a.lat !== 'number' || typeof a.lng !== 'number' || typeof b.lat !== 'number' || typeof b.lng !== 'number') return undefined;
  return haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
}

/** Add a routed set of legs to the cache. Returns the same object when nothing changed. */
export function rememberRoadLegs(
  cache: RoadLegCache,
  stops: TripStop[],
  figures: { distanceKm: number; driveMinutes: number }[]
): RoadLegCache {
  let next: RoadLegCache | null = null;
  for (let i = 1; i < stops.length; i += 1) {
    const f = figures[i - 1];
    const sk = straightKm(stops[i - 1], stops[i]);
    if (!f || !(f.distanceKm > 0) || typeof sk !== 'number') continue;
    const key = pairKey(stops[i - 1], stops[i]);
    const prev = cache[key];
    if (prev && prev.distanceKm === f.distanceKm && prev.driveMinutes === f.driveMinutes) continue;
    if (!next) next = { ...cache };
    next[key] = { distanceKm: f.distanceKm, driveMinutes: f.driveMinutes, straightKm: sk };
  }
  return next ?? cache;
}

/** Road km per straight km, and minutes per road km, across measured legs. */
export function calibration(cache: RoadLegCache): { kmFactor: number; minPerKm: number } | null {
  let road = 0;
  let straight = 0;
  let minutes = 0;
  Object.values(cache).forEach((f) => {
    if (f.straightKm > 1) {
      road += f.distanceKm;
      straight += f.straightKm;
      minutes += f.driveMinutes;
    }
  });
  if (straight <= 0 || road <= 0) return null;
  const kmFactor = Math.min(2, Math.max(1, road / straight));
  return { kmFactor, minPerKm: minutes / road };
}

/** Road legs from the map (TripLeg[], keyed by stop id) → a FigureSet. */
export function figureSetFromLegs(legs: TripLeg[]): FigureSet | null {
  if (!legs.length) return null;
  const ids = [legs[0].fromId, ...legs.map((l) => l.toId)];
  for (let i = 1; i < legs.length; i += 1) {
    if (legs[i].fromId !== legs[i - 1].toId) return null; // not one chain
  }
  return {
    key: ids.join('|'),
    legs: legs.map((l) => ({ distanceKm: l.distanceKm ?? 0, driveMinutes: l.driveMinutes ?? 0, ...(l.via ? { via: l.via } : {}) })),
  };
}

/** Header totals = the sum of the legs, always. */
export function totalsFromLegs(legs: TripLeg[]): { distanceKm?: number; driveMinutes?: number } {
  if (!legs.length) return {};
  let km = 0;
  let min = 0;
  for (const l of legs) {
    if (typeof l.distanceKm !== 'number' || typeof l.driveMinutes !== 'number') return {};
    km += l.distanceKm;
    min += l.driveMinutes;
  }
  return { distanceKm: Math.round(km * 10) / 10, driveMinutes: Math.round(min) };
}

/**
 * How much longer a rig takes than a car on the same road. Guests' times
 * come from a car route (Google): towing a van at 90 km/h on a road a car
 * does at 110 is about 15% slower door to door. Signed in, the route is
 * planned for the rig itself and these factors don't apply.
 */
export const RIG_PACE: Record<string, number> = {
  caravan: 1.15,
  motorhome: 1.08,
  campervan: 1.08,
};

export function rigPaceFactor(vehicleType?: string | null): number {
  return (vehicleType && RIG_PACE[vehicleType]) || 1;
}

/**
 * Car-route legs at the rig's pace. Distances stay; drive minutes stretch.
 * The vehicle route (source 'vehicle') is already the rig's: left alone.
 */
export function paceLegs(legs: TripLeg[], source: LegSource, vehicleType?: string | null): TripLeg[] {
  const k = rigPaceFactor(vehicleType);
  if (k === 1 || source === 'vehicle') return legs;
  return legs.map((l) =>
    typeof l.driveMinutes === 'number' && Number.isFinite(l.driveMinutes)
      ? { ...l, driveMinutes: roundDriveMinutes(l.driveMinutes * k) }
      : l
  );
}
